import React, { useEffect } from 'react';
import { X } from 'lucide-react';
interface LegalModalProps {
  type: 'terms' | 'privacy' | null;
  onClose: () => void;
}
const termsContent = {
  title: 'Términos y Condiciones',
  lastUpdated: 'Enero 2025',
  sections: [{
    heading: '1. Aceptación de los Términos',
    body: 'Al adquirir "ESCAPE 9-A-5: El Método del Teléfono", aceptas estos términos en su totalidad. Si no estás de acuerdo, no realices la compra.'
  }, {
    heading: '2. Naturaleza del Producto',
    body: 'Este producto es una guía digital educativa en formato PDF. Al completar tu pago, recibirás acceso inmediato de descarga. No se envían productos físicos.'
  }, {
    heading: '3. Uso Personal',
    body: 'La guía es para uso personal exclusivo. Está prohibida su reproducción, distribución, reventa o compartición sin autorización escrita. Cada compra es para una sola persona.'
  }, {
    heading: '4. Garantía de Devolución',
    body: 'Ofrecemos una garantía de satisfacción de 30 días. Si no estás satisfecha con el contenido, contáctanos dentro de los 30 días posteriores a la compra y te reembolsaremos el 100% de tu inversión, sin preguntas.'
  }, {
    heading: '5. Descargo de Responsabilidad de Ingresos',
    body: 'Los resultados mencionados en esta página son ejemplos reales pero no típicos. Los ingresos dependen del esfuerzo, la dedicación y las circunstancias individuales de cada persona. No garantizamos ingresos específicos.'
  }, {
    heading: '6. Propiedad Intelectual',
    body: 'Todo el contenido de esta guía, incluyendo textos, estrategias y scripts, es propiedad intelectual de Escape 9-a-5. Queda prohibida su reproducción sin autorización.'
  }, {
    heading: '7. Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las compras realizadas antes de cualquier cambio se rigen por los términos vigentes al momento de la compra.'
  }, {
    heading: '8. Contacto',
    body: 'Para cualquier consulta sobre estos términos, contáctanos a través del correo electrónico indicado en tu confirmación de compra.'
  }]
};
const privacyContent = {
  title: 'Política de Privacidad',
  lastUpdated: 'Enero 2025',
  sections: [{
    heading: '1. Información que Recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago (procesados de forma segura por nuestro proveedor de pagos). No almacenamos información de tarjetas de crédito.'
  }, {
    heading: '2. Uso de tu Información',
    body: 'Usamos tu información exclusivamente para: (a) procesar tu compra y enviarte el producto, (b) enviarte actualizaciones relacionadas con tu compra, y (c) comunicaciones de valor sobre el método si nos das tu consentimiento.'
  }, {
    heading: '3. No Vendemos tus Datos',
    body: 'Tu información personal nunca será vendida, alquilada ni compartida con terceros con fines comerciales. Tu privacidad es una prioridad para nosotros.'
  }, {
    heading: '4. Correos Electrónicos',
    body: 'Al realizar tu compra, podrías recibir correos relacionados con el producto y recursos adicionales. Puedes darte de baja en cualquier momento haciendo clic en "Cancelar suscripción" en cualquier correo.'
  }, {
    heading: '5. Cookies',
    body: 'Este sitio puede usar cookies básicas para mejorar tu experiencia de navegación y para análisis de tráfico. No usamos cookies para rastrear información personal identificable.'
  }, {
    heading: '6. Seguridad',
    body: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información. Los pagos son procesados a través de plataformas certificadas con encriptación SSL.'
  }, {
    heading: '7. Tus Derechos',
    body: 'Tienes derecho a acceder, corregir o solicitar la eliminación de tu información personal en cualquier momento. Contáctanos y procesaremos tu solicitud en un plazo de 30 días.'
  }, {
    heading: '8. Cambios a esta Política',
    body: 'Podemos actualizar esta política ocasionalmente. Te notificaremos por correo electrónico sobre cambios significativos. El uso continuado del sitio implica la aceptación de la política actualizada.'
  }]
};
export function LegalModal({
  type,
  onClose
}: LegalModalProps) {
  const content = type === 'terms' ? termsContent : type === 'privacy' ? privacyContent : null;
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [type]);
  if (!type || !content) return null;
  return <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-104">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" data-id="element-105" />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[90vh] flex flex-col shadow-2xl border border-rose-100 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-106">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-rose-100 bg-brand-light flex-shrink-0" data-id="element-107">
          <div data-id="element-108">
            <h2 className="text-xl font-heading font-bold text-brand-dark" data-id="element-109">
              {content.title}
            </h2>
            <p className="text-xs text-brand-muted mt-0.5" data-id="element-110">
              Última actualización: {content.lastUpdated}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white border border-rose-100 flex items-center justify-center text-brand-muted hover:text-brand-primary hover:border-brand-primary transition-colors" data-id="element-111">
            <X className="w-4 h-4" data-id="element-112" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1" data-id="element-113">
          {content.sections.map((section, i) => <div key={i} data-id="element-114">
              <h3 className="font-bold text-brand-dark mb-2 text-sm uppercase tracking-wide" data-id="element-115">
                {section.heading}
              </h3>
              <p className="text-brand-muted text-sm leading-relaxed" data-id="element-116">
                {section.body}
              </p>
            </div>)}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-rose-100 bg-brand-light flex-shrink-0 text-center" data-id="element-117">
          <button onClick={onClose} className="bg-brand-primary text-white px-8 py-2.5 rounded-full text-sm font-bold hover:bg-rose-700 transition-colors" data-id="element-118">
            Entendido
          </button>
        </div>
      </div>
    </div>;
}